/**
 * Adds a shipping protection line to the cart, priced the way the Navidium app prices it.
 *
 * Taking it off again is the cart's job: `component-cart-items.js` drops the line in the same
 * request as the last item a shopper removes. The charge rules come from the app's own settings,
 * rendered by `snippets/shipping-protection-config.liquid`. Nothing runs without them.
 */
import { Component } from '@theme/component';

export class ShippingProtection extends Component {
  /**
   * The cart's protection line key, empty when the cart carries none.
   *
   * Static because the line has no row of its own to hang a `ref` on, and because
   * `component-cart-items.js` reads it at click time.
   *
   * @type {string}
   */
  static lineKey = '';

  /** @type {{ productId: number, rate: number, minimum: number, maximum: number, variants: Record<string, string>, cart: { basis: number, lineKey: string } }|null} */
  #config = null;

  /**
   * The rungs the app will charge, ascending, in cents. Its ladder is not the product's variant
   * list: it omits tiers the product still carries, and carries one below the minimum.
   *
   * @type {{ price: number, id: number }[]}
   */
  #tiers = [];

  connectedCallback() {
    super.connectedCallback();

    this.#config = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || 'null');
    if (!this.#config?.productId) return;

    this.#tiers = Object.entries(this.#config.variants)
      .map(([price, id]) => ({ price: Math.round(Number(price) * 100), id: Number(id) }))
      .filter((tier) => tier.price >= Math.round(Number(this.#config?.minimum) * 100))
      .sort((a, b) => a.price - b.price);

    document.addEventListener('cart:update', this.#onCartUpdate);

    // Seeded, not acted on. A write here lands as the page becomes interactive, and Shopify
    // serialises cart writes per session, so it would queue the shopper's next add behind it.
    ShippingProtection.lineKey = this.#config.cart.lineKey;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:update', this.#onCartUpdate);
  }

  /**
   * Adds the line when a cart change leaves the cart with something to protect and no protection.
   *
   * @param {Event} event
   */
  #onCartUpdate = async (event) => {
    const { detail } = /** @type {CustomEvent} */ (event);
    if (detail?.data?.didError) return;

    // `/cart/add.js` answers with the lines it added rather than the cart, and some adds are
    // announced with that answer. Only a whole cart carries a token.
    const cart =
      typeof detail?.resource?.token === 'string'
        ? detail.resource
        : await fetch('/cart.js')
            .then((response) => response.json())
            .catch(() => null);
    if (!cart?.items) return;

    const protection = cart.items.filter(
      (/** @type {{ product_id: number }} */ item) => item.product_id === this.#config?.productId,
    );
    const basis = cart.items
      .filter((/** @type {object} */ item) => !protection.includes(item))
      .reduce(
        (/** @type {number} */ total, /** @type {{ final_line_price: number }} */ item) =>
          total + item.final_line_price,
        0,
      );

    ShippingProtection.lineKey = protection[0]?.key ?? '';

    if (basis && !protection.length) this.#addProtection(basis);
  };

  /**
   * Adds the line the app would charge for a cart of this size.
   *
   * Priced once and left alone after that. Re-pricing means removing one variant and appending
   * another, and that renumbers the cart's lines under the row a shopper is clicking remove on.
   *
   * @param {number} basis - What the shopper is paying for everything else, in cents
   */
  async #addProtection(basis) {
    if (!this.#tiers.length || !this.#config) return;

    // The app rounds up to the next rung rather than to the nearest one (`rounding_value`).
    const target = Math.min(basis * (this.#config.rate / 100), this.#config.maximum * 100);
    const tier = this.#tiers.find((rung) => rung.price >= target) ?? this.#tiers[this.#tiers.length - 1];

    const cart = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [tier.id]: 1 } }),
    })
      .then((response) => response.json())
      .catch(() => null);

    ShippingProtection.lineKey =
      cart?.items?.find((/** @type {{ product_id: number }} */ item) => item.product_id === this.#config?.productId)
        ?.key ?? '';
  }
}

if (!customElements.get('shipping-protection')) {
  customElements.define('shipping-protection', ShippingProtection);
}
